import { useEffect, useRef, useState } from "react";
import Input from "@Components/Input";
import styles from "../../PartnerLeadFormModal.module.scss";
import { fetchRegions } from "services/regionsHelper";
import DropdownIcon from "@Icons/DropdownIcon";
import useClickOutside from "hooks/useOutsideClick";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useTranslationHook from "hooks/useTranslationHook";

const RegionDropdown = ({ error, setErrors, selectedRegion, setSelectedRegion }: any) => {
  const [regions, setRegions] = useState<any>([]);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const ref = useRef(null);
  const { t } = useTranslationHook();

  const { partner_id, isLocationSearchRestricted } = useWhitelabel();

  const { inventory, region, filteredUniversity } = useSelector(
    ({ leadFormModal, searchPageData }: AppState) => ({
      inventory: leadFormModal.inventory,
      region: searchPageData.region,
      filteredUniversity: searchPageData.filters.university,
    }),
    shallowEqual,
  );
  const handleSelectRegion = (regionSelected: any) => {
    setSelectedRegion(regionSelected);
    setErrors((errors: any) => ({
      ...errors,
      region: "",
    }));
    setShowOptions(false);
  };

  const getRegions = async (searchName = "") => {
    const options: Record<string, string | number | boolean> = {
      state: "active",
      sort_key: "name",
      sort_order: "asc",
      region_types: "locality,sublocality,establishment",
      ...(partner_id && { partner_id }),
      only: "name,region_type,id,secondary_name,location,canonical_name",
      is_restricted_search_enabled: isLocationSearchRestricted,
      limit: 30,
      // @ts-ignore
      ancestor_id: region?.id || inventory?.region?.id,
    };

    if (searchName) {
      delete options.ancestor_id;
      options.search_name = searchName;
    }
    const response = await fetchRegions(options);
    if (response.message === "success") {
      setRegions(response?.data?.result);
    }
  };

  const fetchData = async (searchName: string) => {
    await getRegions(searchName);
  };

  const debounce = (func: any, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    // eslint-disable-next-line func-names
    return function (...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // eslint-disable-next-line prefer-spread
        func.apply(null, args);
      }, delay);
    };
  };

  const debouncedFetchData = debounce(fetchData, 1000);

  const handleInputChange = (value: string) => {
    debouncedFetchData(value);
  };

  useEffect(() => {
    if (filteredUniversity && filteredUniversity === region.canonical_name) {
      getRegions(region.name);
      setSelectedRegion(region.name);
    } else {
      getRegions();
    }
  }, [filteredUniversity]);

  const handleKeyDown = (e: any) => {
    if (e.key === "Tab") {
      setShowOptions(false);
    }
  };

  useClickOutside(ref, () => setShowOptions(false));

  return (
    <div className={styles.universityDropdownContainer} ref={ref}>
      <Input
        type="dropdown"
        label={t("leadform.regiondropdown.label")}
        placeholder={t("leadform.regiondropdown.placeholder")}
        value={selectedRegion}
        onFocus={() => setShowOptions(true)}
        onKeyDown={(e: any) => handleKeyDown(e)}
        onChange={handleInputChange}
        handleClear={() => {
          setSelectedRegion({ name: "", location: {} });
          setShowOptions(false);
        }}
        clearable
        rightIconClassName={error ? styles.rightIconWithError : ""}
        rightIcon={!selectedRegion && <DropdownIcon size={15} />}
        showErrorMessage={Boolean(error)}
        error={error}
        required
      />
      {showOptions && (
        <div className={styles.universityDropdownOptContainer}>
          {regions.map((value: any) => (
            <div
              key={value.id}
              className={formatClassNames(
                styles.option,
                value.name === selectedRegion ? styles.selected : "",
              )}
              onClick={() => handleSelectRegion(value)}
              role="none"
            >
              <div>{value.name}</div>
              <div className={styles.optionDesc}>{value.secondary_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(RegionDropdown, {
  componentName: "RegionDropdown-Dropdown-PartnerLeadFormModal-Global",
  showForChina: true,
});
